<template>
  <div class="box has-background-grey-darker">
    <!-- Table Controls -->
    <div class="level mb-4">
      <!-- Page Size Selection -->
      <div class="level-left">
        <div class="level-item">
          <div class="select">
            <select v-model="pageSize">
              <option value="100">100 per page</option>
              <option value="500">500 per page</option>
              <option value="1000">1000 per page</option>
            </select>
          </div>
        </div>
        <div class="level-item">
          <span class="has-text-light ml-4">
            {{ currentBets }} of {{ totalBets }} bets
          </span>
        </div>
      </div>
      
      <!-- Pagination -->
      <div class="level-right">
        <div class="level-item">
          <nav class="pagination is-centered" role="navigation">
            <a class="pagination-previous" @click="prevPage" v-if="currentPage === 1" disabled>Previous</a>
            <a class="pagination-previous" @click="prevPage" v-else>Previous</a>
            <div class="pagination-list">
              <input 
                class="input is-small" 
                type="number" 
                v-model="currentPage" 
                :min="1" 
                :max="totalPages"
                style="width: 70px"
              >
              <span class="ml-2 has-text-light">of {{ totalPages }}</span>
            </div>
            <a class="pagination-next" @click="nextPage" v-if="currentPage === totalPages" disabled>Next</a>
            <a class="pagination-next" @click="nextPage" v-else>Next</a>
          </nav>
        </div>
      </div>
    </div>

    <!-- Bets Table -->
    <div class="table-container">
      <table class="table is-fullwidth is-striped has-background-grey-darker">
        <thead>
          <tr>
            <th v-for="header in headers" :key="header.key" @click="sort(header.key)" class="has-text-light is-clickable">
              <div class="is-flex is-align-items-center">
                {{ header.label }}
                <span class="icon is-small ml-2">
                  <i :class="{
                    'fas fa-sort': sortKey !== header.key,
                    'fas fa-sort-up': sortKey === header.key && sortDir === 'asc',
                    'fas fa-sort-down': sortKey === header.key && sortDir === 'desc'
                  }"></i>
                </span>
              </div>
            </th>
            <th v-if="dataSource === 'my'" class="has-text-light"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(bet, index) in paginatedBets" :key="`${bet.created}_${bet.sel_name}_${bet.event}_${index}`">
            <td class="has-text-light">{{ formatRaceDate(bet.the_off) }}</td>
            <td class="has-text-light">{{ formatDate(bet.created) }}</td>
            <td class="has-text-light">{{ bet.sel_name }}</td>
            <td class="has-text-light">{{ bet.event }}</td>
            <td class="has-text-light">{{ bet.bookie_name }}</td>
            <td class="has-text-light editable-cell">
              <input 
                v-if="dataSource === 'my'"
                type="number"
                step="0.01"
                class="odds-input"
                :value="bet.book_odds.toFixed(2)"
                @blur="handleOddsUpdate(bet, 'book_odds', $event)"
                @keydown.enter="handleOddsUpdate(bet, 'book_odds', $event)"
              >
              <span v-else>{{ bet.book_odds.toFixed(2) }}</span>
            </td>
            <td class="has-text-light">{{ bet.bfsp ? bet.bfsp.toFixed(2) : '-' }}</td>
            <td class="has-text-light">{{ bet.win_fair_odds.toFixed(2) }}</td>
            <td class="has-text-light">{{ bet.place_fair_odds.toFixed(2) }}</td>
            <td class="has-text-light">{{ bet.places }} at {{ bet.terms }}</td>
            <td class="has-text-light">{{ bet.ev_taken.toFixed(2) }}%</td>
            <td class="has-text-light editable-cell">
              <input 
                v-if="dataSource === 'my' && stakingMethod === 'recorded'"
                type="number"
                step="0.01"
                class="stake-input"
                :value="Number(bet.stake || 0).toFixed(2)"
                @blur="handleStakeUpdate(bet, 'stake', $event)"
                @keydown.enter="handleStakeUpdate(bet, 'stake', $event)"
              >
              <span v-else>£{{ Number((bet.betDetails?.stake || 0)).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</span>
            </td>
            <td class="has-text-light" :class="getResultClass(bet.result)">
              {{ bet.result || 'Pending' }}
            </td>
            <td :class="{ 'has-text-success': bet.betDetails.profit > 0, 
                         'has-text-danger': bet.betDetails.profit < 0, 
                         'has-text-light': bet.betDetails.profit === 0 }">
              £{{ Number(bet.betDetails.profit).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
            </td>
            <td class="has-text-light">{{ formatPosition(bet.position) }}</td>
            <td class="has-text-light">{{ bet.metric }}</td>
            <td v-if="dataSource === 'my'" class="has-text-light">
              <button 
                class="button is-small is-danger is-outlined delete-btn"
                @click="confirmDelete(bet)"
                title="Delete bet"
              >
                <span class="icon is-small">
                  <i class="fas fa-trash-alt"></i>
                </span>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal" :class="{ 'is-active': showDeleteModal }">
      <div class="modal-background" @click="cancelDelete"></div>
      <div class="modal-card">
        <header class="modal-card-head has-background-grey-darker">
          <p class="modal-card-title has-text-light">
            <span class="icon has-text-danger mr-2">
              <i class="fas fa-exclamation-triangle"></i>
            </span>
            Confirm Delete
          </p>
          <button class="delete" aria-label="close" @click="cancelDelete"></button>
        </header>
        <section class="modal-card-body has-background-grey-dark has-text-light">
          <p class="mb-4">Are you sure you want to delete this bet?</p>
          <div v-if="betToDelete" class="box has-background-grey-darker has-text-light">
            <p><strong class="has-text-info">Horse:</strong> {{ betToDelete.sel_name }}</p>
            <p><strong class="has-text-info">Event:</strong> {{ betToDelete.event }}</p>
            <p><strong class="has-text-info">Date:</strong> {{ formatRaceDate(betToDelete.the_off) }}</p>
            <p><strong class="has-text-info">Stake:</strong> £{{ Number(betToDelete.stake || 0).toFixed(2) }}</p>
          </div>
          <p class="has-text-warning mt-4">
            <span class="icon is-small">
              <i class="fas fa-info-circle"></i>
            </span>
            This action cannot be undone.
          </p>
        </section>
        <footer class="modal-card-foot has-background-grey-darker is-justify-content-flex-end">
          <button class="button" @click="cancelDelete">Cancel</button>
          <button 
            class="button is-danger" 
            @click="executeDelete"
            :class="{ 'is-loading': isDeleting }"
            :disabled="isDeleting"
          >
            <span class="icon is-small">
              <i class="fas fa-trash-alt"></i>
            </span>
            <span>Delete</span>
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'

export default {
  name: 'BetsTable',
  props: {
    bets: {
      type: Array,
      required: true
    },
    filters: {
      type: Object,
      required: true
    },
    bankroll: {
      type: Number,
      default: 0
    },
    kellyFraction: {
      type: Number,
      default: 1
    },
    dataSource: {
      type: String,
      default: 'bb'
    },
    userId: {
      type: Number,
      default: null
    },
    stakingMethod: {
      type: String,
      default: 'recorded'
    }
  },
  emits: ['update-odds', 'update-stake', 'delete-bet'],
  setup(props, { emit }) {
    const pageSize = ref(100)
    const currentPage = ref(1)
    const sortKey = ref('created')
    const sortDir = ref('asc')
    
    // Delete modal state
    const showDeleteModal = ref(false)
    const betToDelete = ref(null)
    const isDeleting = ref(false)

    // Table headers configuration
    const headers = [
      { key: 'the_off', label: 'Race Date' },
      { key: 'created', label: 'Date Placed' },
      { key: 'sel_name', label: 'Horse' },
      { key: 'event', label: 'Event' },
      { key: 'bookie_name', label: 'Bookmaker' },
      { key: 'book_odds', label: 'Odds' },
      { key: 'bfsp', label: 'BFSP' },
      { key: 'win_fair_odds', label: 'Win Fair Odds' },
      { key: 'place_fair_odds', label: 'Place Fair Odds' },
      { key: 'place_terms', label: 'Place Terms' },
      { key: 'ev_taken', label: 'EV %' },
      { key: 'stake', label: 'Total Stake' },
      { key: 'result', label: 'Result' },
      { key: 'profit', label: 'Profit' },
      { key: 'position', label: 'Pos' },
      { key: 'metric', label: 'Metric' }
    ]

    // Sort function
    const sort = (key) => {
      if (sortKey.value === key) {
        sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
      } else {
        sortKey.value = key
        sortDir.value = 'asc'
      }
    }

    // Format date helper for bet placed (full datetime)
    const formatDate = (timestamp) => {
      return new Date(parseInt(timestamp)).toLocaleString()
    }

    // Format race date (DD/MM/YYYY only)
    const formatRaceDate = (timestamp) => {
      if (!timestamp) return '-'
      // Handle both seconds and milliseconds timestamps
      const ts = parseInt(timestamp)
      const date = ts > 9999999999 ? new Date(ts) : new Date(ts * 1000)
      return date.toLocaleDateString('en-GB') // DD/MM/YYYY format
    }

    // Computed properties
    const sortedBets = computed(() => {
      return [...props.bets].sort((a, b) => {
        let aVal, bVal;
        
        // Handle special cases for nested properties
        if (sortKey.value === 'stake') {
          aVal = a.betDetails?.stake || 0;
          bVal = b.betDetails?.stake || 0;
        } else if (sortKey.value === 'profit') {
          aVal = a.betDetails?.profit || 0;
          bVal = b.betDetails?.profit || 0;
        } else if (sortKey.value === 'place_terms') {
          // For place terms, we need to combine places and terms
          aVal = `${a.places} at ${a.terms}`;
          bVal = `${b.places} at ${b.terms}`;
        } else {
          aVal = a[sortKey.value];
          bVal = b[sortKey.value];
        }
        
        // Handle numeric values
        if (!isNaN(parseFloat(aVal))) {
          aVal = parseFloat(aVal);
          bVal = parseFloat(bVal);
        }
        
        if (sortDir.value === 'asc') {
          return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        } else {
          return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
        }
      })
    })

    const totalBets = computed(() => sortedBets.value?.length || 0)
    
    const currentBets = computed(() => {
      const start = (currentPage.value - 1) * pageSize.value
      const end = start + pageSize.value
      const total = sortedBets.value?.length || 0
      return Math.min(pageSize.value, total - start)
    })

    const totalPages = computed(() => {
      return Math.ceil(sortedBets.value.length / pageSize.value)
    })

    const paginatedBets = computed(() => {
      const start = (currentPage.value - 1) * pageSize.value
      const end = start + pageSize.value
      return sortedBets.value.slice(start, end)
    })

    // Navigation methods
    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++
      }
    }

    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--
      }
    }

    // Watch currentPage for manual input
    watch(currentPage, (newPage) => {
      // Ensure page is within bounds
      if (newPage < 1) {
        currentPage.value = 1
      } else if (newPage > totalPages.value) {
        currentPage.value = totalPages.value
      }
    })

    // Watch pageSize to reset currentPage
    watch(pageSize, () => {
      currentPage.value = 1
    })

    const formatPosition = (position) => {
      if (!position) return '-';
      
      // Map specific numbers to text
      const positionMap = {
        '254': 'PU',  // Pulled Up
        '247': 'UR',  // Unseated Rider
        '250': 'F',   // Fell
        '249': 'BD',  // Brought Down
        '99': 'NR'    // Non Runner
      };

      return positionMap[position] || position;
    }

    const getResultClass = (result) => {
      return {
        'has-text-success': result === 'Win',
        'has-text-warning': result.includes('Place'),
        'has-text-danger': result === 'Loss'
      }
    }

    const handleOddsUpdate = (bet, field, event) => {
      const newValue = event.target.value
      const oldValue = bet[field]
      
      // Only emit if value has actually changed
      if (parseFloat(newValue) !== parseFloat(oldValue)) {
        emit('update-odds', {
          betId: bet._id,
          field: field,
          value: newValue
        })
      }
      
      // Blur the input on Enter key
      if (event.type === 'keydown') {
        event.target.blur()
      }
    }

    const handleStakeUpdate = (bet, field, event) => {
      const newValue = event.target.value
      const oldValue = bet[field]
      
      // Only emit if value has actually changed
      if (parseFloat(newValue) !== parseFloat(oldValue)) {
        emit('update-stake', {
          betId: bet._id,
          field: field,
          value: newValue
        })
      }
      
      // Blur the input on Enter key
      if (event.type === 'keydown') {
        event.target.blur()
      }
    }

    // Delete functionality
    const confirmDelete = (bet) => {
      betToDelete.value = bet
      showDeleteModal.value = true
    }

    const cancelDelete = () => {
      showDeleteModal.value = false
      betToDelete.value = null
    }

    const executeDelete = () => {
      if (!betToDelete.value) return
      
      isDeleting.value = true
      
      // Emit delete event with current table state for restoration
      emit('delete-bet', {
        betId: betToDelete.value._id,
        tableState: {
          sortKey: sortKey.value,
          sortDir: sortDir.value,
          currentPage: currentPage.value,
          pageSize: pageSize.value
        }
      })
      
      // Close modal (parent will handle the actual deletion)
      showDeleteModal.value = false
      betToDelete.value = null
      isDeleting.value = false
    }

    // Method to restore table state (called from parent after data refresh)
    const restoreTableState = (state) => {
      if (state.sortKey) sortKey.value = state.sortKey
      if (state.sortDir) sortDir.value = state.sortDir
      if (state.currentPage) currentPage.value = state.currentPage
      if (state.pageSize) pageSize.value = state.pageSize
    }

    return {
      pageSize,
      currentPage,
      sortKey,
      sortDir,
      headers,
      totalPages,
      paginatedBets,
      sort,
      nextPage,
      prevPage,
      formatDate,
      formatRaceDate,
      totalBets,
      currentBets,
      formatPosition,
      getResultClass,
      handleOddsUpdate,
      handleStakeUpdate,
      showDeleteModal,
      betToDelete,
      isDeleting,
      confirmDelete,
      cancelDelete,
      executeDelete,
      restoreTableState,
      dataSource: computed(() => props.dataSource),
      stakingMethod: computed(() => props.stakingMethod)
    }
  }
}
</script>

<style scoped>
.table {
  color: #b5b5b5;
}

.table th {
  color: #ffffff !important;
  border-color: #4a4a4a;
}

.table td {
  border-color: #4a4a4a;
}

.table.is-striped tbody tr:not(.is-selected):nth-child(2n) {
  background-color: #2f2f2f !important;
}

.table tr:hover {
  background-color: #363636 !important;
}

.is-clickable {
  cursor: pointer;
}

.pagination-previous,
.pagination-next {
  background-color: #363636;
  color: #b5b5b5;
  border-color: #4a4a4a;
}

.pagination-previous:hover,
.pagination-next:hover {
  background-color: #4a4a4a;
  color: #ffffff;
}

.pagination-previous[disabled],
.pagination-next[disabled] {
  background-color: #2b2b2b;
  color: #666666;
}

.input {
  background-color: #363636 !important;
  color: #b5b5b5 !important;
  border-color: #4a4a4a !important;
  text-align: center;
}

.select select {
  background-color: #363636 !important;
  color: #b5b5b5 !important;
  border-color: #4a4a4a !important;
}

.editable-cell {
  padding: 0.5rem 0.75rem;
}

.odds-input {
  width: 100%;
  max-width: 80px;
  background-color: #2b2b2b !important;
  color: #b5b5b5 !important;
  border: 1px solid #4a4a4a !important;
  border-radius: 3px;
  padding: 0.25rem 0.5rem;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.odds-input:hover {
  border-color: #00d1b2 !important;
  background-color: #363636 !important;
}

.odds-input:focus {
  outline: none;
  border-color: #00d1b2 !important;
  background-color: #363636 !important;
  box-shadow: 0 0 0 2px rgba(0, 209, 178, 0.2);
}

/* Remove spinner arrows from number input */
.odds-input::-webkit-outer-spin-button,
.odds-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.odds-input[type=number] {
  -moz-appearance: textfield;
  appearance: textfield;
}

.stake-input {
  width: 100%;
  max-width: 80px;
  background-color: #2b2b2b !important;
  color: #b5b5b5 !important;
  border: 1px solid #4a4a4a !important;
  border-radius: 3px;
  padding: 0.25rem 0.5rem;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.stake-input:hover {
  border-color: #00d1b2 !important;
  background-color: #363636 !important;
}

.stake-input:focus {
  outline: none;
  border-color: #00d1b2 !important;
  background-color: #363636 !important;
  box-shadow: 0 0 0 2px rgba(0, 209, 178, 0.2);
}

/* Remove spinner arrows from number input */
.stake-input::-webkit-outer-spin-button,
.stake-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.stake-input[type=number] {
  -moz-appearance: textfield;
  appearance: textfield;
}

/* Delete button styles */
.delete-btn {
  padding: 0.25rem 0.5rem;
  transition: all 0.2s ease;
}

.delete-btn:hover {
  background-color: #f14668 !important;
  color: #fff !important;
}

/* Modal styles */
.modal-card {
  max-width: 500px;
  border-radius: 6px;
  overflow: hidden;
}

.modal-card-head {
  border-bottom: 1px solid #4a4a4a;
}

.modal-card-foot {
  border-top: 1px solid #4a4a4a;
  gap: 0.5rem;
}

.modal-card-foot .button {
  min-width: 100px;
}

.modal-background {
  background-color: rgba(0, 0, 0, 0.7);
}
</style>